/**
 * The global Animation Library (SPEC "Animation Library", "Model Animation Assignment").
 *
 * Clips live once, globally; a project only references clip ids. Everything here is a thin
 * orchestration over `clipsRepo` / `libraryIO` plus the store cache the panels render from.
 */

import { AppError, DEFAULT_FOLDER, newId } from '../../shared';
import type { CanonicalAnimationClip } from '../../shared';
import type { LibraryClipSummary } from '../types';
import {
  clipsRepo,
  commitLibraryImport,
  exportClipsJson,
  exportLibraryJson,
  folderOf,
  previewLibraryImport,
  pruneFolderAssignments,
  // Aliased: the controller exposes methods with the same four names, and an unaliased import
  // inside those method bodies reads like a recursive call even though it is not.
  createFolder as createFolderMeta,
  deleteFolder as deleteFolderMeta,
  moveClipToFolder as moveClipToFolderMeta,
  renameFolder as renameFolderMeta,
} from '../../persistence';
import type { LibraryImportMode, LibraryImportPreview, LibraryImportResult } from '../../persistence';
import { downloadBlob } from '../../importExport';
import type { ControllerDeps } from './internal';
import { guard } from './internal';
import type { LibraryController } from './types';

/** Frozen at the pre-rename spelling so library files already on disk keep one recognisable name. */
export const LIBRARY_JSON_FILENAME = 'animstatex-library.json';

/**
 * Ask before throwing away unsaved work. Falls back to "yes" outside a browser (node tests),
 * where there is no user to ask — callers that care pass their own.
 */
function defaultConfirm(message: string): boolean {
  if (typeof window === 'undefined' || typeof window.confirm !== 'function') return true;
  return window.confirm(message);
}

/**
 * `"Idle"` → `"Idle 2"` → `"Idle 3"` when the library already holds that name.
 *
 * Library names are unique (audit P1) and `clipsRepo` enforces that at the transaction. This is
 * the UI-side pre-pick against the store's cached list, so a generated name — "new clip", a
 * duplicate — arrives free instead of arriving as an error the user did nothing to cause.
 */
export function uniqueClipName(name: string, taken: readonly string[]): string {
  const trimmed = name.trim() || 'new clip';
  const used = new Set(taken);
  if (!used.has(trimmed)) return trimmed;
  for (let n = 2; n < 1000; n++) {
    const candidate = `${trimmed} ${n}`;
    if (!used.has(candidate)) return candidate;
  }
  return `${trimmed} ${Date.now()}`;
}

/** The characters Windows and macOS refuse in a file name. */
const FORBIDDEN_FILENAME_CHARS = /[\\/:*?"<>|]/;

/**
 * A clip name turned into something every filesystem accepts.
 *
 * Only the characters Windows / macOS actually refuse are replaced (the control range included —
 * a clip name arrives from a JSON file as often as from a keyboard). The fallback exists because
 * a name made entirely of forbidden characters would otherwise download as ".json".
 */
export function safeFileName(name: string): string {
  let cleaned = '';
  for (const ch of name) {
    // Per character rather than one regex class: the control range would have to be spelled with
    // escapes inside the class, and a literal control byte in this source file is a trap.
    cleaned += FORBIDDEN_FILENAME_CHARS.test(ch) || ch.charCodeAt(0) < 0x20 ? '_' : ch;
  }
  const trimmed = cleaned.trim();
  return trimmed.length > 0 ? trimmed : 'clip';
}

/** `"clips-set"` → `"clips-set.json"`; an extension the user already typed is left alone. */
export function ensureJsonExt(filename: string): string {
  return /\.json$/i.test(filename) ? filename : `${filename}.json`;
}

export function createLibraryController(deps: ControllerDeps): LibraryController {
  const { ctx } = deps;
  const { store } = ctx;

  const libraryNames = (): string[] => store.getState().library.clips.map((c) => c.name);

  /**
   * The open clip when it holds edits the library does not have yet, otherwise `null` — the single
   * gate all three export surfaces share (the third, GLB, has its own copy in `exportController`).
   *
   * A CLEAN open clip is byte-identical to its record, so returning `null` for it costs nothing
   * and keeps every "this clip is no longer in the library" guard honest.
   */
  const dirtyOpenClip = (): CanonicalAnimationClip | null => {
    const state = store.getState();
    return state.clip && state.clipDirty ? state.clip : null;
  };

  /**
   * The clip on SCREEN beats the clip on disk, for the clip/set downloads.
   *
   * Clips are not auto-saved, so `state.clip` and `clipsRepo` drift apart the moment the user sets
   * a key. Before this, both downloads read the repository only: the audit reproduced it live
   * (§2.2 #1) — keys on screen, `clipDirty === true`, one keyframe in the store, and the JSON the
   * user handed their game carried the last SAVED shape with nothing said about it. The GLB half
   * of the same defect is fixed the same way in `exportController.exportGlbBuffer`; this is its
   * twin, deliberately identical in the two decisions that matter:
   *
   *  - **Dirty only.** A clean open clip is byte-identical to its record, so reading the record
   *    costs nothing and keeps `CLIP_NOT_FOUND` honest: a clean clip missing from the repository
   *    really was deleted underneath us, and the download must still refuse rather than quietly
   *    serve the in-memory copy of something no longer in the library.
   *  - **In memory only.** `exportClipsJson` writes nothing, and neither does this. Downloading a
   *    file is not a save; `newClip()` marks a clip dirty immediately, so saving here would push
   *    half-authored clips into the library behind the user's back.
   *
   * `note` is appended to the method's own toast rather than raised as a second one — `ui.toast`
   * is a single slot (`actions/context.ts`), so a follow-up notification would erase the line that
   * says what was downloaded. It is empty unless the dirty clip is actually among `clipIds`: a
   * clip that is not being exported has nothing to warn about.
   */
  const liveClipOverride = (
    clipIds: readonly string[],
  ): { overrides: readonly CanonicalAnimationClip[]; note: string } => {
    const open = dirtyOpenClip();
    if (!open) return { overrides: [], note: '' };
    return {
      overrides: [open],
      note: clipIds.includes(open.id) ? ` Includes unsaved changes to "${open.name}".` : '',
    };
  };

  const controller: LibraryController = {
    async refresh(): Promise<void> {
      return guard(ctx, 'The animation library could not be read.', async () => {
        const clips = await clipsRepo.list();
        const known = new Set(clips.map((c) => c.id));

        // Prune before resolving: a clip deleted while its folder assignment stayed behind would
        // otherwise keep that folder looking occupied forever. `pruneFolderAssignments` returns
        // the state it left behind, so this is one read, not two.
        const folderState = await pruneFolderAssignments([...known]);

        const summaries: LibraryClipSummary[] = clips.map((clip) => ({
          id: clip.id,
          name: clip.name,
          fps: clip.fps,
          durationFrames: clip.durationFrames,
          updatedAt: clip.updatedAt,
          folder: folderOf(folderState, clip.id),
        }));

        store.batch(() => {
          store.actions.setLibraryClips(summaries);
          store.actions.setLibraryFolders(folderState.folders);
          // The selection is NOT re-read from the project record here. `openProject` hydrates it
          // (projectController), and re-reading it on every library write raced a just-queued
          // save: `saveProjectState` is a read-modify-write on the same record, so a refresh that
          // landed between the store change and the write put the OLD selection back. The cached
          // selection is the live one; all this does is drop ids that no longer exist.
          const selected = store.getState().library.selectedClipIds;
          const kept = selected.filter((id) => known.has(id));
          if (kept.length !== selected.length) store.actions.setSelectedClipIds(kept);
        });
      });
    },

    newClip(name: string, options?: { confirm?: (message: string) => boolean }): void {
      const state = store.getState();
      if (state.clip && state.clipDirty) {
        const ask = options?.confirm ?? defaultConfirm;
        if (!ask(`Discard unsaved changes to "${state.clip.name}" and start a new clip?`)) return;
      }
      store.actions.newClip(uniqueClipName(name, libraryNames()));
    },

    async saveCurrentClip(): Promise<void> {
      return guard(ctx, 'The animation could not be saved.', async () => {
        const clip = store.getState().clip;
        if (!clip) {
          throw new AppError('NO_CLIP', 'There is no animation open to save.');
        }
        await clipsRepo.put(clip);
        store.actions.markClipSaved();
        await controller.refresh();
        store.actions.toast('info', `"${clip.name}" saved to the library.`);
      });
    },

    async saveClipAs(name?: string): Promise<void> {
      return guard(ctx, 'The animation could not be saved under a new name.', async () => {
        const clip = store.getState().clip;
        if (!clip) {
          throw new AppError('NO_CLIP', 'There is no animation open to save.');
        }
        // Read BEFORE the write, because `openClip` below moves the editor to the copy and this
        // question is about the state the user pressed the button in.
        //
        // NOT because a refresh would reload it: `refresh()` deliberately does NOT re-read the
        // project record any more. That read raced the project save chain — `saveProjectState` is
        // a read-modify-write, so a refresh landing between the store change and the write put the
        // OLD selection back. Do not restore it here or in `refresh()`; the cached selection is
        // the live one, hydrated only by `openProject` and reset by `loadIntoSession`.
        const originalWasAssigned = store.getState().library.selectedClipIds.includes(clip.id);

        const taken = libraryNames();
        const wanted = name?.trim();
        if (wanted && taken.includes(wanted)) {
          // A typed name is the user's to fix — the same rule as `setClipName`, reported here
          // rather than as a surprise rejection from the transaction below.
          throw new AppError(
            'DUPLICATE_CLIP_NAME',
            `The library already has an animation called "${wanted}".`,
            { name: wanted },
          );
        }
        const newName = wanted || uniqueClipName(`${clip.name} copy`, taken);

        const now = Date.now();
        const saved = await clipsRepo.put({
          ...clip,
          id: newId(),
          name: newName,
          createdAt: now,
          updatedAt: now,
        });
        // Continue in the copy, not in the original: "save as" means the next Ctrl+S goes to the
        // new entry. `openClip` clears the undo history and releases IK — the same trade every
        // open makes — but it also snaps the playhead to frame 0, which for a save the user fired
        // MID-EDIT is a lost place rather than a fresh start. The clone has the same duration, so
        // the frame is still valid; put it back.
        const frame = store.getState().playhead.frame;
        store.actions.openClip(saved);
        // Safe right after `openClip`: `livePose` is exactly the clip evaluated at frame 0, so
        // `setPlayhead` sees no unkeyed pose to rescue and adds neither a toast nor an undo entry.
        if (frame !== 0) store.actions.setPlayhead(frame);
        await controller.refresh();

        // The stale-export trap made visible: the project still exports the ORIGINAL clip, so a
        // user who saves-as and keeps editing would otherwise ship the version they left behind.
        const assignmentNote = originalWasAssigned
          ? ` This project still has "${clip.name}" assigned for export.`
          : '';
        store.actions.toast('info', `"${saved.name}" saved to the library.${assignmentNote}`);
      });
    },

    async loadClip(id: string): Promise<void> {
      return guard(ctx, 'The animation could not be loaded.', async () => {
        const clip = await clipsRepo.get(id);
        if (!clip) {
          throw new AppError('CLIP_NOT_FOUND', `No animation with id "${id}".`, { id });
        }
        store.actions.openClip(clip);
      });
    },

    async renameClip(id: string, name: string): Promise<void> {
      return guard(ctx, 'The animation could not be renamed.', async () => {
        const renamed = await clipsRepo.rename(id, name);
        await controller.refresh();
        const open = store.getState().clip;
        // Re-open only when there is nothing to lose: `openClip` resets the undo stack.
        if (open && open.id === id && !store.getState().clipDirty) {
          store.actions.openClip(renamed);
        }
      });
    },

    async duplicateClip(id: string): Promise<void> {
      return guard(ctx, 'The animation could not be duplicated.', async () => {
        const source = store.getState().library.clips.find((c) => c.id === id);
        const wanted = source ? `${source.name} copy` : undefined;
        const copy = await clipsRepo.duplicate(
          id,
          wanted ? uniqueClipName(wanted, libraryNames()) : undefined,
        );
        await controller.refresh();
        store.actions.toast('info', `Duplicated as "${copy.name}".`);
      });
    },

    async deleteClip(id: string): Promise<void> {
      return guard(ctx, 'The animation could not be deleted.', async () => {
        await clipsRepo.removeClipEverywhere(id);
        await controller.refresh();
        if (store.getState().clip?.id === id) {
          // SPEC keeps the user's work in the editor; it is simply no longer saved anywhere.
          store.actions.markClipDirty();
          store.actions.toast(
            'info',
            'The open animation was deleted from the library; it is still open but unsaved.',
          );
        }
      });
    },

    async exportJson(): Promise<void> {
      return guard(ctx, 'The animation library could not be exported.', async () => {
        /*
         * The backup carries the clip on SCREEN too (audit §2.2 #1, third surface). A user who
         * exports the library to keep their work safe and gets the last-saved shape of the clip
         * they are looking at has lost exactly the edits they were trying to protect, and nothing
         * said so.
         *
         * `exportLibraryJson` applies an override REPLACE-ONLY: a clip that is not in the library
         * is not put into a library file just because it happens to be open. See the reasoning at
         * that function. The note below is therefore two different facts, never both.
         */
        const live = dirtyOpenClip();
        const text = await exportLibraryJson({ overrides: live ? [live] : [] });
        downloadBlob(new Blob([text], { type: 'application/json' }), LIBRARY_JSON_FILENAME);
        if (!live) return;

        // Asked of the repository, not of `library.clips`: a delete from a second tab leaves the
        // cache still listing a clip the export (which reads the repository) has already dropped,
        // and a backup must never claim to hold something it does not.
        const inLibrary = (await clipsRepo.get(live.id)) !== undefined;
        // This path had no toast at all, and it stays silent when there is nothing to report — a
        // download announces itself. When there IS something, it is one toast: `ui.toast` is a
        // single slot, so a second one would only erase the first.
        store.actions.toast(
          'info',
          inLibrary
            ? `Library exported. Includes unsaved changes to "${live.name}".`
            : `Library exported. "${live.name}" is not in the library, so this backup does not ` +
              'include it.',
        );
      });
    },

    async previewImport(text: string): Promise<LibraryImportPreview> {
      return guard(ctx, 'The library file could not be read.', () => previewLibraryImport(text));
    },

    async commitImport(text: string, mode: LibraryImportMode): Promise<LibraryImportResult> {
      return guard(ctx, 'The library file could not be imported.', async () => {
        const preview = await previewLibraryImport(text);
        if (!preview.ok) {
          throw new AppError(
            'INVALID_LIBRARY_FILE',
            `This library file cannot be imported: ${preview.errors.join('; ')}`,
            { errors: preview.errors },
          );
        }
        const result = await commitLibraryImport(preview.file, mode);
        await controller.refresh();
        const renamed =
          result.renamed.length > 0
            ? ` Renamed ${result.renamed.length} to keep names unique ` +
              `(${result.renamed.map((r) => `"${r.from}" → "${r.to}"`).join(', ')}).`
            : '';
        store.actions.toast(
          'info',
          `Imported ${result.imported.length}, overwrote ${result.overwritten.length}, ` +
            `skipped ${result.skipped.length}, copied ${result.copied.length}.${renamed}`,
        );
        return result;
      });
    },

    async toggleSelected(clipId: string): Promise<void> {
      return guard(ctx, 'The animation selection could not be saved.', async () => {
        store.actions.toggleClipSelected(clipId);
        await deps.controllers.project.saveProjectState();
      });
    },

    // --- folders ------------------------------------------------------------------------------
    //
    // Every one of these is the same three steps: the data layer writes (and is the only place
    // that validates a name), then `refresh()` re-reads the whole library so the folder list, the
    // per-clip folder and the orphan prune all come from one read, then a toast. Nothing here
    // asks the user anything — the panel owns the prompts, because a merge confirmation has to
    // happen before the write and the data layer merges silently on purpose.

    async createFolder(name: string): Promise<void> {
      return guard(ctx, 'The folder could not be created.', async () => {
        await createFolderMeta(name);
        await controller.refresh();
        store.actions.toast('info', `Folder "${name.trim()}" created.`);
      });
    },

    async renameFolder(from: string, to: string): Promise<void> {
      return guard(ctx, 'The folder could not be renamed.', async () => {
        await renameFolderMeta(from, to);
        await controller.refresh();
        store.actions.toast('info', `Folder "${from.trim()}" renamed to "${to.trim()}".`);
      });
    },

    async deleteFolder(name: string): Promise<void> {
      return guard(ctx, 'The folder could not be deleted.', async () => {
        // Counted from the cache *before* the write: after `refresh()` the members are already in
        // General and indistinguishable from clips that were never in the folder.
        const moved = store.getState().library.clips.filter((c) => c.folder === name).length;
        await deleteFolderMeta(name);
        await controller.refresh();
        store.actions.toast(
          'info',
          `Folder "${name.trim()}" deleted — ${moved} clips moved to ${DEFAULT_FOLDER}.`,
        );
      });
    },

    async moveClipToFolder(clipId: string, folder: string): Promise<void> {
      return guard(ctx, 'The animation could not be moved.', async () => {
        await moveClipToFolderMeta(clipId, folder);
        await controller.refresh();
        // No toast: the row the user changed already shows the new folder, and moving several
        // clips in a row would otherwise be a stack of notifications saying what they can see.
      });
    },

    // --- game-clip downloads ------------------------------------------------------------------

    async downloadClipFiles(clipIds: readonly string[]): Promise<void> {
      return guard(ctx, 'The animation files could not be downloaded.', async () => {
        const names = new Map(store.getState().library.clips.map((c) => [c.id, c.name]));
        const live = liveClipOverride(clipIds);
        for (const id of clipIds) {
          // One file per clip, each exported on its own so the file the game loads carries exactly
          // the clip its name promises. The open clip's overrides ride along on every call: an
          // override for an id this file is not about is never read.
          const json = await exportClipsJson([id], { overrides: live.overrides });
          downloadBlob(
            new Blob([json], { type: 'application/json' }),
            `${safeFileName(names.get(id) ?? id)}.json`,
          );
        }
        // Unconditional, and deliberately phrased as a hint rather than a result: Chrome asks
        // before the second download of a batch, and whether the user allowed it is not
        // observable from JS. Claiming "3 files downloaded" would be a guess.
        store.actions.toast(
          'info',
          `${clipIds.length} file(s) — allow multiple downloads if the browser asks.${live.note}`,
        );
      });
    },

    async downloadClipSet(clipIds: readonly string[], filename: string): Promise<void> {
      return guard(ctx, 'The clip set could not be downloaded.', async () => {
        const live = liveClipOverride(clipIds);
        const json = await exportClipsJson(clipIds, { overrides: live.overrides });
        // Sanitized here rather than at the two call sites: the name arrives either from a folder
        // ("Folder as set" sends `<folder>.json`, and a folder called "Idle/Run" is legal) or
        // straight from a prompt. `safeFileName` first so the extension it appends cannot be
        // swallowed by a stem that ended in a forbidden character.
        const download = ensureJsonExt(safeFileName(filename));
        downloadBlob(new Blob([json], { type: 'application/json' }), download);
        store.actions.toast('info', `Set "${filename}" — ${clipIds.length} clips.${live.note}`);
      });
    },
  };

  return controller;
}
